import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { TbDots } from 'react-icons/tb';
import { isNavItemActive, MOBILE_NAV_ORDER, NAV_ITEMS, Sidebar } from './Sidebar';
import { NotificationsBell } from '../community/NotificationsBell';
import { useAuth } from '../auth/AuthContext';
import { LogoutButton } from '../auth/LogoutButton';
import { InstallPrompt } from '../pwa/InstallPrompt';
import './layout.css';

// Breakpoint 768px: below it the shell uses the mobile navigation.
export const NARROW_QUERY = '(max-width: 767.98px)';

function matchesNarrow() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(NARROW_QUERY).matches;
}

function MobileBrand() {
  return <span className="gym-mobile-brand">
    <img className="gym-mobile-brand__logo" src={`${process.env.PUBLIC_URL || ''}/brand/hammer-mark.png`} alt="" width="40" height="40" />
    <span className="gym-mobile-brand__word">HAMMER</span>
  </span>;
}

/**
 * Tracks the viewport breakpoint. Read synchronously on first render so a
 * phone never flashes the desktop column (and vice versa). Falls back to the
 * legacy `addListener` API for older Safari.
 */
function useIsNarrow() {
  const [narrow, setNarrow] = useState(matchesNarrow);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(NARROW_QUERY);
    const handleChange = (event) => setNarrow(event.matches);
    setNarrow(media.matches);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  return narrow;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal sheet rises from the bottom. Focus moves inside on open, Tab and
 * Shift+Tab cycle within it, Escape or the backdrop close it, and page scroll
 * is locked while it is open. Focus restoration lives in `AppShell`, which
 * owns the button that opened it.
 */
const ROLE_LABELS = { admin: 'Recepción', user: 'Alumno', coach: 'Profesor' };

function MoreSheet({ role, activePath, onClose, onNavigate }) {
  const panelRef = useRef(null);
  const { user } = useAuth();
  const items = NAV_ITEMS[role] || [];
  const displayName = (user && (user.username || user.email)) || '';

  useEffect(() => {
    const panel = panelRef.current;
    const first = panel && panel.querySelector(FOCUSABLE);
    if (first) first.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusables = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const firstItem = focusables[0];
      const lastItem = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === firstItem || !panel.contains(active))) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && (active === lastItem || !panel.contains(active))) {
        event.preventDefault();
        firstItem.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div className="gym-more-root">
      <div className="gym-more-backdrop" data-testid="gym-more-backdrop" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        id="gym-more-panel"
        className="gym-more-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Menú"
      >
        <div className="gym-more-sheet__header">
          <MobileBrand />
          <button type="button" className="gym-more-sheet__close" aria-label="Cerrar menú" onClick={onClose}>Cerrar</button>
        </div>
        <div className="gym-more-sheet__account">
          <span className="gym-identity__avatar" aria-hidden="true">{displayName.trim().charAt(0).toUpperCase() || '?'}</span>
          <span className="gym-identity__text">
            <span className="gym-identity__name">{displayName}</span>
            <span className="gym-identity__role">{ROLE_LABELS[role]}</span>
            {(role === 'admin' || role === 'coach') && <Link className="gym-identity__link" to="/cambiar-password" onClick={onNavigate}>Cambiar contraseña</Link>}
          </span>
        </div>
        <InstallPrompt variant="compact" />
        <nav aria-label="Todas las secciones" className="gym-more-sheet__nav">
          <ul className="gym-more-list">
            {items.map((item) => {
              const { to, label, icon: Icon } = item;
              const isCurrent = isNavItemActive(item, activePath);
              return <li key={to}>
                <Link to={to} onClick={onNavigate} aria-current={isCurrent ? 'page' : undefined}>
                  <Icon aria-hidden="true" focusable="false" />
                  <span>{label}</span>
                  {isCurrent && <span className="gym-more-list__current">Actual</span>}
                </Link>
              </li>;
            })}
          </ul>
        </nav>
        <div className="gym-more-sheet__logout"><LogoutButton variant="dark" /></div>
      </div>
    </div>
  );
}

function MobileNavigation({ role, pathname, open, onMore, moreButtonRef }) {
  const items = (MOBILE_NAV_ORDER[role] || [])
    .map((to) => (NAV_ITEMS[role] || []).find((item) => item.to === to))
    .filter(Boolean)
    .map((item) => ({ ...item, featured: item.to === '/mi-cuenta/entrenar' }));
  const isMoreCurrent = !items.some((item) => isNavItemActive(item, pathname));
  return <nav className={`gym-bottom-nav${items.length === 3 ? ' gym-bottom-nav--compact' : ''}`} aria-label="Principal">
    {items.map((item) => {
      const { to, label, icon: Icon, featured } = item;
      const isCurrent = isNavItemActive(item, pathname);
      return <Link
      key={to}
      to={to}
      aria-current={isCurrent ? 'page' : undefined}
      className={`gym-bottom-nav__item${featured ? ' gym-bottom-nav__item--featured' : ''}${isCurrent ? ' is-active' : ''}`}
    >
      <Icon aria-hidden="true" focusable="false" />
      <span>{label}</span>
      </Link>;
    })}
    <button ref={moreButtonRef} type="button" className={`gym-bottom-nav__item${isMoreCurrent ? ' is-active' : ''}`} aria-label="Más" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? 'gym-more-panel' : undefined} aria-current={isMoreCurrent ? 'page' : undefined} onClick={onMore}>
      <TbDots aria-hidden="true" focusable="false" />
      <span>Más</span>
    </button>
  </nav>;
}

/**
 * Authenticated layout for one role: a fixed 17rem column on the left from
 * 768px up, and below that a top bar, bottom navigation and "Más" sheet.
 * Pages render
 * through `<Outlet />`.
 */
export function AppShell({ role }) {
  const narrow = useIsNarrow();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const moreButtonRef = useRef(null);
  const wasOpen = useRef(false);

  const closeDrawer = useCallback(() => setOpen(false), []);

  // Leaving phone width with the "Más" sheet open must not leave a modal behind.
  useEffect(() => {
    if (!narrow) setOpen(false);
  }, [narrow]);

  // Return focus to "Más" when the sheet closes on a phone.
  useEffect(() => {
    if (narrow && wasOpen.current && !open && moreButtonRef.current) moreButtonRef.current.focus();
    wasOpen.current = open;
  }, [narrow, open]);

  return (
    <div className={narrow ? 'gym-shell gym-shell--narrow' : 'gym-shell'}>
      {narrow ? (
        <header className="gym-topbar">
          <MobileBrand />
          <NotificationsBell />
        </header>
      ) : (
        <aside className="gym-shell__sidebar">
          <Sidebar role={role} />
        </aside>
      )}

      {narrow && open && (
        <MoreSheet role={role} activePath={pathname} onClose={closeDrawer} onNavigate={closeDrawer} />
      )}

      <main className="gym-shell__main">
        {!narrow && <header className="gym-shell__header"><NotificationsBell /></header>}
        <div className="gym-shell__content">
          <Outlet />
        </div>
      </main>
      {narrow && <MobileNavigation role={role} pathname={pathname} open={open} onMore={() => setOpen(true)} moreButtonRef={moreButtonRef} />}
    </div>
  );
}
